import { Link } from "react-router-dom";
import { useScrollAnimation, fadeInUp, staggerContainer, AnimatedDiv, AnimatedH2, AnimatedP } from "@/hooks/useScrollAnimation";
import { Home, TrendingUp, MapPin, Sun, Mountain, GraduationCap } from "lucide-react";

const HomePageSEOContent = () => {
  const { ref, controls } = useScrollAnimation();

  const communities = [
    { name: "St. George", path: "/st-george-real-estate" },
    { name: "Washington", path: "/washington-ut-real-estate" },
    { name: "Hurricane", path: "/hurricane-real-estate" },
    { name: "Ivins", path: "/ivins-real-estate" },
    { name: "Santa Clara", path: "/santa-clara-real-estate" },
    { name: "La Verkin", path: "/la-verkin-real-estate" },
    { name: "Cedar City", path: "/cedar-city-real-estate" },
    { name: "Kanab", path: "/kanab-real-estate" },
  ];

  const highlights = [
    {
      icon: Sun,
      title: "300+ Days of Sunshine",
      description: "St. George enjoys one of the best climates in Utah with warm, sunny weather year-round — perfect for outdoor living and an active lifestyle.",
    },
    {
      icon: TrendingUp,
      title: "Strong Market Growth",
      description: "As one of America's fastest-growing metro areas, St. George real estate offers strong appreciation and long-term investment potential for homeowners.",
    },
    {
      icon: Mountain,
      title: "World-Class Recreation",
      description: "Minutes from Zion National Park, Snow Canyon, and Sand Hollow — St. George is a paradise for hiking, golf, mountain biking, and rock climbing.",
    },
    {
      icon: Home,
      title: "Diverse Housing Options",
      description: "From luxury estates with red rock views to family-friendly neighborhoods and 55+ communities, St. George has homes for every lifestyle and budget.",
    },
    {
      icon: MapPin,
      title: "Prime Southern Utah Location",
      description: "Centrally located in Washington County with easy access to Las Vegas, Salt Lake City, and multiple national parks — an ideal home base.",
    },
    {
      icon: GraduationCap,
      title: "Growing Economy & Education",
      description: "Utah Tech University, Intermountain Health, and a booming tech sector fuel job growth, making St. George attractive for families and professionals.",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <AnimatedDiv
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
        >
          {/* Main SEO Heading */}
          <div className="text-center mb-12 md:mb-16">
            <AnimatedH2
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl heading-luxury text-foreground mb-6"
            >
              St. George Real Estate — Your Guide to Southern Utah Homes
            </AnimatedH2>
            <AnimatedP
              variants={fadeInUp}
              className="text-muted-foreground text-base md:text-lg max-w-3xl mx-auto leading-relaxed"
            >
              Whether you're buying your first home, upgrading to a luxury property, or relocating to Southern Utah, 
              St. George real estate offers unmatched value, lifestyle, and opportunity. Explore homes for sale 
              across Washington County with the guidance of a trusted local expert.
            </AnimatedP>
          </div>

          {/* Highlights Grid */}
          <AnimatedDiv
            variants={fadeInUp}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16"
          >
            {highlights.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 p-6 rounded-xl bg-muted/50 border border-border hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-foreground font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </AnimatedDiv>

          {/* Rich Text SEO Content */}
          <AnimatedDiv
            variants={fadeInUp}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="prose prose-lg max-w-none">
              <h3 className="text-2xl md:text-3xl heading-luxury text-foreground mb-4">
                Why Buy a Home in St. George, Utah?
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                St. George has become one of the most sought-after real estate markets in the western United States. 
                Nestled among the stunning red rock formations of Southern Utah, this vibrant city combines small-town 
                charm with modern amenities, making it an ideal place to call home. The St. George real estate market 
                features everything from contemporary condos and townhomes near downtown to sprawling luxury estates 
                in master-planned communities like Entrada, SunRiver, and Stone Cliff.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                With no state income tax, a cost of living below many western metros, and a median home price that 
                offers strong value compared to neighboring states, St. George attracts retirees, remote workers, 
                young families, and investors alike. The area's real estate market has shown consistent appreciation 
                driven by population growth, limited buildable land, and exceptional quality of life.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Buyers searching for homes for sale in St. George will find diverse neighborhoods — from the historic 
                charm of downtown to family-friendly communities in Little Valley and Desert Hills, and the resort-style 
                living of Bloomington and Sunbrook. Each area offers unique advantages, and working with a local agent 
                who understands the micro-markets, HOA structures, water rights, and upcoming developments is essential 
                to making a smart investment.
              </p>

              <h3 className="text-2xl md:text-3xl heading-luxury text-foreground mb-4 mt-10">
                Selling Your Home in St. George
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you're considering selling your home in the St. George real estate market, strategic pricing and 
                professional marketing are key to maximizing your return. The market attracts significant out-of-state 
                interest — particularly from buyers relocating from California, the Pacific Northwest, and the Midwest. 
                Professional photography, drone videography, virtual tours, and targeted digital advertising help your 
                listing reach the right audience at the right time.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Neil Gibbs brings over 32 years of local real estate expertise to every transaction, providing sellers 
                with accurate market analysis, custom marketing strategies, and expert negotiation skills. Whether you're 
                selling a starter home in Washington or a luxury property in Entrada, Neil's hands-on approach ensures 
                a smooth, profitable sale. <Link to="/propertyvaluation" className="text-primary hover:text-primary/80 font-medium underline">Request a free home valuation</Link> to 
                get started.
              </p>
            </div>
          </AnimatedDiv>

          {/* Community Links */}
          <AnimatedDiv variants={fadeInUp} className="text-center">
            <h3 className="text-2xl heading-luxury text-foreground mb-6">
              Explore Southern Utah Communities
            </h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Discover homes for sale across Washington County and beyond. Each community offers a unique lifestyle in the heart of Southern Utah.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {communities.map((community) => (
                <Link
                  key={community.name}
                  to={community.path}
                  className="px-5 py-2.5 rounded-full border border-border bg-background text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all duration-300 text-sm font-medium"
                >
                  {community.name} Real Estate
                </Link>
              ))}
            </div>
          </AnimatedDiv>
        </AnimatedDiv>
      </div>
    </section>
  );
};

export default HomePageSEOContent;
